import React, { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../../contexts/ToastContext.jsx';
import { ApiError } from '../../utils/apiClient.js';
import { parseApiError } from '../../utils/consolidatedErrorHandling.js';
import { apiRequest } from '../../utils/apiClient.js';

/**
 * Modal for creating new users
 */
const UserCreationModal = ({ isOpen, onClose, onUserCreated }) => {
  const { getToken } = useAuth();
  const { showToast } = useToast();
  
  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    mobile_phone: '',
    subscription_plan: 'free',
    send_welcome_email: true
  });
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  /**
   * Validate form data
   */
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    
    // Phone number validation (optional but must be valid if provided)
    if (formData.mobile_phone.trim() && !/^\+?[\d\s\-\(\)]+$/.test(formData.mobile_phone)) {
      newErrors.mobile_phone = 'Please enter a valid phone number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form input changes
   */
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      const token = await getToken();
      
      const response = await apiRequest('/api/admin/users', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorMessage = await parseApiError(response);
        throw new ApiError(errorMessage, response.status, response);
      }

      const newUser = await response.json();
      
      showToast('User created successfully', 'success');
      onUserCreated(newUser);
      handleClose();

    } catch (error) {
      console.error('Error creating user:', error);
      
      // Handle specific error cases
      if (error.status === 409) {
        setErrors({ email: 'A user with this email already exists' });
      } else {
        showToast(error.message || 'Failed to create user', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reset form and close modal
   */
  const handleClose = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      mobile_phone: '',
      subscription_plan: 'free',
      send_welcome_email: true
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-medium text-white dark:text-white">Create New User</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-white dark:text-white"
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* First Name */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1 dark:text-gray-600">
              First Name *
            </label>
            <input
              type="text"
              value={formData.first_name}
              onChange={(e) => handleInputChange('first_name', e.target.value)}
              className={`w-full px-3 py-2 bg-gray-700 border rounded-md text-white focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                errors.first_name ? 'border-red-500' : 'border-gray-600'
              }`}
              placeholder="Enter first name"
            />
            {errors.first_name && (
              <p className="text-red-400 text-sm mt-1">{errors.first_name}</p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1 dark:text-gray-600">
              Last Name *
            </label>
            <input
              type="text"
              value={formData.last_name}
              onChange={(e) => handleInputChange('last_name', e.target.value)}
              className={`w-full px-3 py-2 bg-gray-700 border rounded-md text-white focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                errors.last_name ? 'border-red-500' : 'border-gray-600'
              }`}
              placeholder="Enter last name"
            />
            {errors.last_name && (
              <p className="text-red-400 text-sm mt-1">{errors.last_name}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1 dark:text-gray-600">
              Email Address *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={`w-full px-3 py-2 bg-gray-700 border rounded-md text-white focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                errors.email ? 'border-red-500' : 'border-gray-600'
              }`}
              placeholder="Enter email address"
            />
            {errors.email && (
              <p className="text-red-400 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1 dark:text-gray-600">
              Phone Number
            </label>
            <input
              type="tel"
              value={formData.mobile_phone}
              onChange={(e) => handleInputChange('mobile_phone', e.target.value)}
              className={`w-full px-3 py-2 bg-gray-700 border rounded-md text-white focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                errors.mobile_phone ? 'border-red-500' : 'border-gray-600'
              }`}
              placeholder="Enter phone number (optional)"
            />
            {errors.mobile_phone && (
              <p className="text-red-400 text-sm mt-1">{errors.mobile_phone}</p>
            )}
          </div>

          {/* Subscription Plan */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1 dark:text-gray-600">
              Initial Subscription Plan
            </label>
            <select
              value={formData.subscription_plan}
              onChange={(e) => handleInputChange('subscription_plan', e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-teal-500 dark:text-white"
            >
              <option value="free">Free</option>
              <option value="basic">Basic</option>
              <option value="pro">Pro</option>
              <option value="premium">Premium</option>
            </select>
          </div>

          {/* Welcome Email */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="send_welcome_email"
              checked={formData.send_welcome_email}
              onChange={(e) => handleInputChange('send_welcome_email', e.target.checked)}
              className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-600 rounded bg-gray-700"
            />
            <label htmlFor="send_welcome_email" className="ml-2 text-sm text-gray-300 dark:text-gray-600">
              Send welcome email to user
            </label>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors dark:text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed dark:text-white"
            >
              {loading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserCreationModal;
